import { getCoverThumbUrl } from '../utils/coverUrl';
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Search as SearchIcon, Filter, X, Star, BookOpen, SlidersHorizontal } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Button } from '../components/ui/button';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || window.location.origin;
const API = `${BACKEND_URL}/api`;

const getAssetBaseUrl = () => window.location.origin;

const getCoverUrl = (url) => {
  if (!url) return 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&q=80';
  if (url.startsWith('http')) return url;
  if (url.startsWith('/api')) return `${getAssetBaseUrl()}${url}`;
  return url;
};

const Search = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  
  const [query, setQuery] = useState(searchParams.get('q') || '');
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [totalResults, setTotalResults] = useState(0);
  
  // Filters
  const [showFilters, setShowFilters] = useState(false);
  const [genres, setGenres] = useState([]);
  const [authors, setAuthors] = useState([]);
  const [selectedGenre, setSelectedGenre] = useState(searchParams.get('genre') || '');
  const [selectedAuthor, setSelectedAuthor] = useState(searchParams.get('author') || '');
  const [minRating, setMinRating] = useState(searchParams.get('min_rating') || '');
  const [sortBy, setSortBy] = useState(searchParams.get('sort') || 'created_at');
  const [sortOrder, setSortOrder] = useState(searchParams.get('order') || 'desc');

  // Fetch filter options
  useEffect(() => {
    const fetchFilters = async () => {
      try {
        const [genresRes, authorsRes] = await Promise.all([
          axios.get(`${API}/books/genres`),
          axios.get(`${API}/books/authors`)
        ]);
        setGenres(genresRes.data || []);
        setAuthors(authorsRes.data || []);
      } catch (error) {
        console.error('Error fetching filters:', error);
      }
    };
    fetchFilters();
  }, []);

  const performSearch = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (query) params.append('q', query);
      if (selectedGenre) params.append('genre', selectedGenre);
      if (selectedAuthor) params.append('author', selectedAuthor);
      if (minRating) params.append('min_rating', minRating);
      params.append('sort_by', sortBy);
      params.append('sort_order', sortOrder);
      
      const response = await axios.get(`${API}/books/search?${params.toString()}`);
      setResults(response.data.books || []);
      setTotalResults(response.data.total || 0);
    } catch (error) {
      console.error('Search error:', error);
      setResults([]);
      setTotalResults(0);
    } finally {
      setLoading(false);
    }
  }, [query, selectedGenre, selectedAuthor, minRating, sortBy, sortOrder]);

  // Search on mount and when filters change
  useEffect(() => {
    performSearch();
  }, [performSearch]);

  const handleSearch = (e) => {
    e.preventDefault();
    // Update URL params
    const params = new URLSearchParams();
    if (query) params.set('q', query);
    if (selectedGenre) params.set('genre', selectedGenre);
    if (selectedAuthor) params.set('author', selectedAuthor);
    if (minRating) params.set('min_rating', minRating);
    if (sortBy !== 'created_at') params.set('sort', sortBy);
    if (sortOrder !== 'desc') params.set('order', sortOrder);
    setSearchParams(params);
  };

  const clearFilters = () => {
    setSelectedGenre('');
    setSelectedAuthor('');
    setMinRating('');
    setSortBy('created_at');
    setSortOrder('desc');
    setSearchParams(query ? { q: query } : {});
  };

  const hasActiveFilters = selectedGenre || selectedAuthor || minRating || sortBy !== 'created_at';

  return (
    <div className="min-h-screen bg-black pt-20">
      <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-4xl font-bold text-white mb-2">Search Books</h1>
          <p className="text-gray-400">Find your next great read</p>
        </div>

        {/* Search Bar */}
        <form onSubmit={handleSearch} className="mb-8">
          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <SearchIcon className="absolute left-4 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
              <Input
                type="text"
                placeholder="Search by title, author, or description..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                className="pl-12 py-6 bg-white/10 border-white/20 text-white placeholder:text-gray-400 focus:border-red-600 text-lg"
                data-testid="search-input"
              />
            </div>
            <Button
              type="submit"
              className="bg-red-600 hover:bg-red-700 text-white px-8 py-6"
              data-testid="search-btn"
            >
              <SearchIcon className="h-5 w-5 mr-2" />
              Search
            </Button>
            <Button
              type="button"
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              className={`border-white/20 text-white hover:bg-white/10 px-6 py-6 ${hasActiveFilters ? 'border-red-500' : ''}`}
              data-testid="filter-toggle-btn"
            >
              <SlidersHorizontal className="h-5 w-5 mr-2" />
              Filters
              {hasActiveFilters && <span className="ml-2 bg-red-600 text-white text-xs px-2 py-0.5 rounded-full">Active</span>}
            </Button>
          </div>
        </form>

        {/* Filters Panel */}
        {showFilters && (
          <div className="bg-white/5 border border-white/10 rounded-xl p-6 mb-8" data-testid="filters-panel">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-white flex items-center">
                <Filter className="h-5 w-5 mr-2" />
                Filter & Sort
              </h3>
              {hasActiveFilters && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-gray-400 hover:text-white"
                >
                  <X className="h-4 w-4 mr-1" />
                  Clear All
                </Button>
              )}
            </div>
            
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
              {/* Genre Filter */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Genre</label>
                <select
                  value={selectedGenre}
                  onChange={(e) => setSelectedGenre(e.target.value)}
                  className="w-full h-10 px-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-red-500 focus:outline-none"
                  data-testid="genre-filter"
                >
                  <option value="" className="bg-gray-900">All Genres</option>
                  {genres.map((genre) => (
                    <option key={genre} value={genre} className="bg-gray-900">{genre}</option>
                  ))}
                </select>
              </div>

              {/* Author Filter */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Author</label>
                <select
                  value={selectedAuthor}
                  onChange={(e) => setSelectedAuthor(e.target.value)}
                  className="w-full h-10 px-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-red-500 focus:outline-none"
                  data-testid="author-filter"
                >
                  <option value="" className="bg-gray-900">All Authors</option>
                  {authors.map((author) => (
                    <option key={author} value={author} className="bg-gray-900">{author}</option>
                  ))}
                </select>
              </div>

              {/* Min Rating Filter */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Min Rating</label>
                <select
                  value={minRating}
                  onChange={(e) => setMinRating(e.target.value)}
                  className="w-full h-10 px-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-red-500 focus:outline-none"
                  data-testid="rating-filter"
                >
                  <option value="" className="bg-gray-900">Any Rating</option>
                  <option value="4" className="bg-gray-900">4+ Stars</option>
                  <option value="3" className="bg-gray-900">3+ Stars</option>
                  <option value="2" className="bg-gray-900">2+ Stars</option>
                </select>
              </div>

              {/* Sort By */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Sort By</label>
                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="w-full h-10 px-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-red-500 focus:outline-none"
                  data-testid="sort-filter"
                >
                  <option value="created_at" className="bg-gray-900">Date Added</option>
                  <option value="title" className="bg-gray-900">Title</option>
                  <option value="author" className="bg-gray-900">Author</option>
                  <option value="rating" className="bg-gray-900">Rating</option>
                  <option value="publish_year" className="bg-gray-900">Year</option>
                </select>
              </div>

              {/* Sort Order */}
              <div>
                <label className="text-sm text-gray-400 mb-2 block">Order</label>
                <select
                  value={sortOrder}
                  onChange={(e) => setSortOrder(e.target.value)}
                  className="w-full h-10 px-3 bg-white/5 border border-white/10 rounded-lg text-white focus:border-red-500 focus:outline-none"
                  data-testid="order-filter"
                >
                  <option value="desc" className="bg-gray-900">Descending</option>
                  <option value="asc" className="bg-gray-900">Ascending</option>
                </select>
              </div>
            </div>

            <div className="mt-4 flex justify-end">
              <Button onClick={performSearch} className="bg-red-600 hover:bg-red-700">
                Apply Filters
              </Button>
            </div>
          </div>
        )}

        {/* Results Count */}
        <div className="mb-6">
          <p className="text-gray-400">
            {loading ? 'Searching...' : `${totalResults} book${totalResults !== 1 ? 's' : ''} found`}
          </p>
        </div>

        {/* Results Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-white text-xl">Searching...</div>
          </div>
        ) : results.length > 0 ? (
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 gap-4 sm:gap-6">
            {results.map((book) => (
              <div
                key={book.id}
                onClick={() => navigate(`/book/${book.id}`)}
                className="group cursor-pointer"
                data-testid="search-result-card"
              >
                <div className="relative rounded-lg overflow-hidden transition-transform duration-300 group-hover:scale-105">
                  <img
                    src={getCoverThumbUrl(book.cover_image_url)}
                    alt={book.title}
                    className="w-full aspect-[2/3] object-cover"
                    onError={(e) => {
                      e.target.src = 'https://images.unsplash.com/photo-1544947950-fa07a98d237f?w=400&q=80';
                    }}
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black via-transparent to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                    <div className="absolute bottom-0 left-0 right-0 p-3">
                      <h3 className="text-sm font-semibold text-white truncate">{book.title}</h3>
                      <p className="text-xs text-gray-300 truncate">{book.author}</p>
                      <div className="flex items-center mt-1">
                        <Star className="h-3 w-3 text-yellow-400 fill-yellow-400 mr-1" />
                        <span className="text-xs text-yellow-400">{book.rating?.toFixed(1) || '0.0'}</span>
                        <span className="text-xs text-gray-400 ml-2">({book.total_reviews || 0})</span>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="mt-2">
                  <h3 className="text-sm font-medium text-white truncate">{book.title}</h3>
                  <p className="text-xs text-gray-400 truncate">{book.author}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="text-center py-20">
            <BookOpen className="h-16 w-16 text-gray-600 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No books found</h3>
            <p className="text-gray-400">Try adjusting your search or filters</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Search;
